import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  addSuite as addSuiteRequest,
  deleteUnit as deleteUnitRequest,
  fetchSuiteCounts as fetchSuiteCountsRequest,
  fetchSuiteWalkThroughs as fetchSuiteWalkThroughsRequest,
  getAssetsBySuiteId as getAssetsBySuiteIdRequest,
  getPropertySuiteById as getPropertySuiteByIdRequest,
  onFetchMoreDetails as onFetchMoreDetailsRequest,
  reRenderKeyPickupPdf as reRenderKeyPickupPdfRequest,
  updateSuite as updateSuiteRequest,
} from 'src/services/client';
import {
  Asset,
  Counts,
  Suite,
  WalkThrough,
} from 'src/SharedCustomHooks/UseSuiteActions/types';

interface UseSuiteAPIState {
  suite: Suite | null;
  isLoadingSuite: boolean;
  suiteError: string | null;

  counts: Counts | null;
  isLoadingCounts: boolean;
  countsError: string | null;

  walkThroughs: WalkThrough[];
  isLoadingWalkThroughs: boolean;
  walkThroughsError: string | null;

  assets: Asset[];
  isLoadingAssets: boolean;
  assetsError: string | null;

  moreDetails: any | null;
  isLoadingMoreDetails: boolean;
  moreDetailsError: string | null;

  isCreatingSuite: boolean;
  createError: string | null;

  isUpdatingSuite: boolean;
  updateError: string | null;

  isDeletingSuite: boolean;
  deleteError: string | null;

  isReRenderingKeyPickupPdf: boolean;
  reRenderKeyPickupPdfError: string | null;
}

export interface FetchSuiteResult {
  ok: boolean;
  suite: Suite | null;
}

export interface FetchSuiteCountsResult {
  ok: boolean;
  counts: Counts | null;
}

export interface FetchWalkThroughsResult {
  ok: boolean;
  walkThroughs: WalkThrough[];
}

export interface FetchAssetsResult {
  ok: boolean;
  assets: Asset[];
}

export interface FetchMoreDetailsResult {
  ok: boolean;
  details: any | null;
}

export interface CreateSuiteResult {
  ok: boolean;
  suite: Suite | null;
}

export interface UpdateSuiteResult {
  ok: boolean;
  suite: Suite | null;
}

export interface DeleteSuiteResult {
  ok: boolean;
}

export interface ReRenderKeyPickupPdfResult {
  ok: boolean;
}

export interface UseSuiteAPIReturn {
  suite: Suite | null;
  isLoadingSuite: boolean;
  suiteError: string | null;
  fetchSuiteById: (suiteId: string) => Promise<FetchSuiteResult>;
  setSuite: (suite: Suite | null) => void;
  clearSuiteError: () => void;

  counts: Counts | null;
  isLoadingCounts: boolean;
  countsError: string | null;
  fetchSuiteCounts: (
    suiteId: string,
    role: string
  ) => Promise<FetchSuiteCountsResult>;
  clearCountsError: () => void;

  walkThroughs: WalkThrough[];
  isLoadingWalkThroughs: boolean;
  walkThroughsError: string | null;
  fetchSuiteWalkThroughs: (
    suiteId: string,
    role: string
  ) => Promise<FetchWalkThroughsResult>;
  clearWalkThroughsError: () => void;

  assets: Asset[];
  isLoadingAssets: boolean;
  assetsError: string | null;
  fetchAssetsBySuiteId: (
    suiteId: string,
    userId: string,
    role: string
  ) => Promise<FetchAssetsResult>;
  clearAssetsError: () => void;

  moreDetails: any | null;
  isLoadingMoreDetails: boolean;
  moreDetailsError: string | null;
  fetchMoreDetails: (suiteId: string) => Promise<FetchMoreDetailsResult>;
  clearMoreDetailsError: () => void;

  isCreatingSuite: boolean;
  createError: string | null;
  createSuite: (suiteData: any) => Promise<CreateSuiteResult>;
  clearCreateError: () => void;

  isUpdatingSuite: boolean;
  updateError: string | null;
  updateSuite: (suiteData: any) => Promise<UpdateSuiteResult>;
  clearUpdateError: () => void;

  isDeletingSuite: boolean;
  deleteError: string | null;
  deleteSuite: (suiteId: string, userId: string) => Promise<DeleteSuiteResult>;
  clearDeleteError: () => void;

  isReRenderingKeyPickupPdf: boolean;
  reRenderKeyPickupPdfError: string | null;
  reRenderKeyPickupPdf: (
    suiteId: string
  ) => Promise<ReRenderKeyPickupPdfResult>;
  clearReRenderKeyPickupPdfError: () => void;
}

const unwrapArrayPayload = <T,>(result: any): T[] => {
  if (Array.isArray(result)) {
    return result;
  }

  return result?.data || [];
};

const extractApiErrorMessage = async (
  response: any,
  fallback: string
): Promise<string> => {
  const data: any = await response.json().catch(() => ({}));
  return data?.message || fallback;
};

export const useSuiteAPI = (): UseSuiteAPIReturn => {
  const [state, setState] = useState<UseSuiteAPIState>({
    suite: null,
    isLoadingSuite: false,
    suiteError: null,

    counts: null,
    isLoadingCounts: false,
    countsError: null,

    walkThroughs: [],
    isLoadingWalkThroughs: false,
    walkThroughsError: null,

    assets: [],
    isLoadingAssets: false,
    assetsError: null,

    moreDetails: null,
    isLoadingMoreDetails: false,
    moreDetailsError: null,

    isCreatingSuite: false,
    createError: null,

    isUpdatingSuite: false,
    updateError: null,

    isDeletingSuite: false,
    deleteError: null,

    isReRenderingKeyPickupPdf: false,
    reRenderKeyPickupPdfError: null,
  });

  const fetchSuiteById = async (
    suiteId: string
  ): Promise<FetchSuiteResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isLoadingSuite: true,
      suiteError: null,
    }));

    try {
      const response: any = await getPropertySuiteByIdRequest(suiteId);

      if (response.ok || response.status === 200) {
        const suite: Suite = await response.json();

        setState((prev: UseSuiteAPIState) => ({
          ...prev,
          suite,
          isLoadingSuite: false,
        }));

        return { ok: true, suite };
      }

      const errorMessage: string = await extractApiErrorMessage(
        response,
        `Failed to fetch suite: ${response.status}`
      );

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isLoadingSuite: false,
        suiteError: errorMessage,
      }));

      return { ok: false, suite: null };
    } catch (err: any) {
      console.error('useSuiteAPI fetchSuiteById failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch suite details';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isLoadingSuite: false,
        suiteError: errorMessage,
      }));

      return { ok: false, suite: null };
    }
  };

  const fetchSuiteCounts = async (
    suiteId: string,
    role: string
  ): Promise<FetchSuiteCountsResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isLoadingCounts: true,
      countsError: null,
    }));

    try {
      const response: any = await fetchSuiteCountsRequest(suiteId, role);

      if (!response.ok) {
        throw new Error(`Failed to fetch suite counts: ${response.status}`);
      }

      const result: any = await response.json();

      if (result.statusCode !== 200) {
        throw new Error('Invalid response from suite counts API');
      }

      const counts: Counts = result.data;

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        counts,
        isLoadingCounts: false,
      }));

      return { ok: true, counts };
    } catch (err: any) {
      console.error('useSuiteAPI fetchSuiteCounts failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch suite counts';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isLoadingCounts: false,
        countsError: errorMessage,
      }));

      return { ok: false, counts: null };
    }
  };

  const fetchSuiteWalkThroughs = async (
    suiteId: string,
    role: string
  ): Promise<FetchWalkThroughsResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isLoadingWalkThroughs: true,
      walkThroughsError: null,
    }));

    try {
      const response: any = await fetchSuiteWalkThroughsRequest(suiteId, role);

      if (!response.ok) {
        throw new Error(`Failed to fetch walkthroughs: ${response.status}`);
      }

      const result: any = await response.json();
      const walkThroughs: WalkThrough[] = unwrapArrayPayload<WalkThrough>(result);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        walkThroughs,
        isLoadingWalkThroughs: false,
      }));

      return { ok: true, walkThroughs };
    } catch (err: any) {
      console.error('useSuiteAPI fetchSuiteWalkThroughs failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch walkthroughs';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isLoadingWalkThroughs: false,
        walkThroughsError: errorMessage,
      }));

      return { ok: false, walkThroughs: [] };
    }
  };

  const fetchAssetsBySuiteId = async (
    suiteId: string,
    userId: string,
    role: string
  ): Promise<FetchAssetsResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isLoadingAssets: true,
      assetsError: null,
    }));

    try {
      const response: any = await getAssetsBySuiteIdRequest(
        suiteId,
        userId,
        role
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.status}`);
      }

      const result: any = await response.json();
      const assets: Asset[] = unwrapArrayPayload<Asset>(result);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        assets,
        isLoadingAssets: false,
      }));

      return { ok: true, assets };
    } catch (err: any) {
      console.error('useSuiteAPI fetchAssetsBySuiteId failed:', err);

      const errorMessage: string = err?.message || 'Failed to fetch assets';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isLoadingAssets: false,
        assetsError: errorMessage,
      }));

      return { ok: false, assets: [] };
    }
  };

  const fetchMoreDetails = async (
    suiteId: string
  ): Promise<FetchMoreDetailsResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isLoadingMoreDetails: true,
      moreDetailsError: null,
    }));

    try {
      const response: any = await onFetchMoreDetailsRequest(suiteId);

      if (!response.ok) {
        throw new Error(`Failed to fetch more details: ${response.status}`);
      }

      const result: any = await response.json();
      const details: any = result?.data || result || null;

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        moreDetails: details,
        isLoadingMoreDetails: false,
      }));

      return { ok: true, details };
    } catch (err: any) {
      console.error('useSuiteAPI fetchMoreDetails failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch more details';

      setState((prev) => ({
        ...prev,
        isLoadingMoreDetails: false,
        moreDetailsError: errorMessage,
      }));

      return { ok: false, details: null };
    }
  };

  const createSuite = async (suiteData: any): Promise<CreateSuiteResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isCreatingSuite: true,
      createError: null,
    }));

    try {
      const response: any = await addSuiteRequest(suiteData);

      if (!response.ok) {
        throw new Error(`Failed to add suite: ${response.status}`);
      }

      const result: any = await response.json();

      if (result.statusCode === 402) {
        throw new Error('That unit number already exists, please try another.');
      }

      if (result.statusCode === 409 || result.statusCode === 400) {
        throw new Error('User already exists with this email address.');
      }

      if (result.statusCode !== 200) {
        throw new Error('Something went wrong with adding the suite.');
      }

      toast.success('ⓘ Suite has been added.');

      const suite: Suite = result.data;

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isCreatingSuite: false,
      }));

      return { ok: true, suite };
    } catch (err: any) {
      console.error('useSuiteAPI createSuite failed:', err);

      const errorMessage: string = err?.message || 'Failed to add suite';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isCreatingSuite: false,
        createError: errorMessage,
      }));

      return { ok: false, suite: null };
    }
  };

  const updateSuite = async (suiteData: any): Promise<UpdateSuiteResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isUpdatingSuite: true,
      updateError: null,
    }));

    try {
      const response: any = await updateSuiteRequest(suiteData);

      if (!response.ok) {
        throw new Error(`Failed to update suite: ${response.status}`);
      }

      const result: any = await response.json();

      if (result.statusCode === 402) {
        throw new Error('That unit number already exists, please try another.');
      }

      if (result.statusCode === 409 || result.statusCode === 400) {
        throw new Error('User already exists with this email address.');
      }

      if (result.statusCode !== 200) {
        throw new Error('Something went wrong with updating the suite.');
      }

      toast.success('ⓘ Suite has been updated.');

      const suite: Suite = result.data;

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        suite,
        isUpdatingSuite: false,
      }));

      return { ok: true, suite };
    } catch (err: any) {
      console.error('useSuiteAPI updateSuite failed:', err);

      const errorMessage: string = err?.message || 'Failed to update suite';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isUpdatingSuite: false,
        updateError: errorMessage,
      }));

      return { ok: false, suite: null };
    }
  };

  const deleteSuite = async (
    suiteId: string,
    userId: string
  ): Promise<DeleteSuiteResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isDeletingSuite: true,
      deleteError: null,
    }));

    try {
      const response: any = await deleteUnitRequest(suiteId, userId);

      if (!response.ok) {
        throw new Error(`Failed to delete suite: ${response.status}`);
      }

      toast.success('ⓘ Unit has been deleted.');

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isDeletingSuite: false,
      }));

      return { ok: true };
    } catch (err: any) {
      console.error('useSuiteAPI deleteSuite failed:', err);

      const errorMessage: string = err?.message || 'Failed to delete suite';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isDeletingSuite: false,
        deleteError: errorMessage,
      }));

      return { ok: false };
    }
  };

  const reRenderKeyPickupPdf = async (
    suiteId: string
  ): Promise<ReRenderKeyPickupPdfResult> => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      isReRenderingKeyPickupPdf: true,
      reRenderKeyPickupPdfError: null,
    }));

    try {
      const response: any = await reRenderKeyPickupPdfRequest(suiteId);

      if (response.status !== 200 && !response.ok) {
        throw new Error(
          `KeyHandOff process submit failed. (${response.status})`
        );
      }

      toast.success('ⓘ Key Pickup Confirmation re-sent');

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isReRenderingKeyPickupPdf: false,
      }));

      return { ok: true };
    } catch (err: any) {
      console.error('useSuiteAPI reRenderKeyPickupPdf failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to re-render key pickup PDF';

      setState((prev: UseSuiteAPIState) => ({
        ...prev,
        isReRenderingKeyPickupPdf: false,
        reRenderKeyPickupPdfError: errorMessage,
      }));

      return { ok: false };
    }
  };

  const setSuite = (suite: Suite | null) => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, suite }));
  };

  const clearSuiteError = () => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, suiteError: null }));
  };

  const clearCountsError = () => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, countsError: null }));
  };

  const clearWalkThroughsError = () => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      walkThroughsError: null,
    }));
  };

  const clearAssetsError = () => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, assetsError: null }));
  };

  const clearMoreDetailsError = () => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, moreDetailsError: null }));
  };

  const clearCreateError = () => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, createError: null }));
  };

  const clearUpdateError = () => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, updateError: null }));
  };

  const clearDeleteError = () => {
    setState((prev: UseSuiteAPIState) => ({ ...prev, deleteError: null }));
  };

  const clearReRenderKeyPickupPdfError = () => {
    setState((prev: UseSuiteAPIState) => ({
      ...prev,
      reRenderKeyPickupPdfError: null,
    }));
  };

  return {
    suite: state.suite,
    isLoadingSuite: state.isLoadingSuite,
    suiteError: state.suiteError,
    fetchSuiteById,
    setSuite,
    clearSuiteError,

    counts: state.counts,
    isLoadingCounts: state.isLoadingCounts,
    countsError: state.countsError,
    fetchSuiteCounts,
    clearCountsError,

    walkThroughs: state.walkThroughs,
    isLoadingWalkThroughs: state.isLoadingWalkThroughs,
    walkThroughsError: state.walkThroughsError,
    fetchSuiteWalkThroughs,
    clearWalkThroughsError,

    assets: state.assets,
    isLoadingAssets: state.isLoadingAssets,
    assetsError: state.assetsError,
    fetchAssetsBySuiteId,
    clearAssetsError,

    moreDetails: state.moreDetails,
    isLoadingMoreDetails: state.isLoadingMoreDetails,
    moreDetailsError: state.moreDetailsError,
    fetchMoreDetails,
    clearMoreDetailsError,

    isCreatingSuite: state.isCreatingSuite,
    createError: state.createError,
    createSuite,
    clearCreateError,

    isUpdatingSuite: state.isUpdatingSuite,
    updateError: state.updateError,
    updateSuite,
    clearUpdateError,

    isDeletingSuite: state.isDeletingSuite,
    deleteError: state.deleteError,
    deleteSuite,
    clearDeleteError,

    isReRenderingKeyPickupPdf: state.isReRenderingKeyPickupPdf,
    reRenderKeyPickupPdfError: state.reRenderKeyPickupPdfError,
    reRenderKeyPickupPdf,
    clearReRenderKeyPickupPdfError,
  };
};
